import * as React from 'react';
import { FC, Fragment, useMemo, useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    EditButton,
    useNotify,
    useDelete,
    useRefresh,
    ListProps,
    FieldProps,
    Button as RAButton,
    useRecordContext,
    Filter,
    SelectInput,
} from 'react-admin';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField as MuiTextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { IProductRecycle, UserRole } from '../types/types';
import useAuthorize from '../hooks/useAuthorize';
import { useRecycleSellerContext } from './useRecycleSellerContext';
import { postRecycleAction } from './RecycleProductFormFields';

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
    draft: 'default',
    submitted: 'warning',
    approved: 'success',
    rejected: 'error',
    archived: 'info',
};

const StatusField: FC<FieldProps> = () => {
    const record = useRecordContext<IProductRecycle>();
    if (!record) return null;
    if (record.is_deleted) {
        return <Chip size="small" label="Deleted" color="default" sx={{ opacity: 0.85 }} />;
    }
    if (!record.status) return null;
    return <Chip size="small" label={record.status} color={STATUS_COLORS[record.status] || 'default'} />;
};

const EditRecycleButton: FC = () => {
    const record = useRecordContext<IProductRecycle>();
    if (!record || record.is_deleted) return null;
    return <EditButton />;
};

const deletedRowStyle = (record?: IProductRecycle) =>
    record?.is_deleted
        ? { opacity: 0.55, backgroundColor: '#f0f0f0', color: '#757575' }
        : {};

const RecycleListFilters = () => (
    <Filter>
        <SelectInput
            source="status"
            label="Status"
            choices={[
                { id: 'draft', name: 'Draft' },
                { id: 'submitted', name: 'Submitted' },
                { id: 'approved', name: 'Approved' },
                { id: 'rejected', name: 'Rejected' },
                { id: 'archived', name: 'Archived' },
            ]}
            alwaysOn
        />
    </Filter>
);

const ApproveButton: FC = () => {
    const record = useRecordContext<IProductRecycle>();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    if (!record || record.status !== 'submitted' || record.is_deleted) return null;

    const handleApprove = async () => {
        setLoading(true);
        try {
            const response = await postRecycleAction(`/productrecycle/${record.id}/approve`);
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Approve failed');
            }
            notify('Listing approved', { type: 'success' });
            window.location.reload();
        } catch (error: any) {
            notify(error.message || 'Approve failed', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <RAButton label="Approve" onClick={handleApprove} startIcon={<CheckIcon />} disabled={loading} />
    );
};

const RejectButton: FC = () => {
    const record = useRecordContext<IProductRecycle>();
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    if (!record || record.status !== 'submitted' || record.is_deleted) return null;

    const handleReject = async () => {
        setLoading(true);
        try {
            const response = await postRecycleAction(`/productrecycle/${record.id}/reject`, {
                rejection_reason: reason,
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Reject failed');
            }
            notify('Listing rejected', { type: 'info' });
            setOpen(false);
            window.location.reload();
        } catch (error: any) {
            notify(error.message || 'Reject failed', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <RAButton label="Reject" onClick={() => setOpen(true)} startIcon={<CloseIcon />} disabled={loading} />
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Reject listing</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Provide a reason for the seller.
                    </DialogContentText>
                    <MuiTextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Rejection reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleReject} color="error" disabled={!reason.trim() || loading}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

const DeleteWithConfirmButton: FC = () => {
    const record = useRecordContext<IProductRecycle>();
    const [deleteOne, { isLoading }] = useDelete();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record || record.is_deleted) return null;

    const confirmDelete = async () => {
        try {
            await deleteOne('productrecycle', { id: record.id });
            notify('Listing deleted', { type: 'info' });
            setOpen(false);
            refresh();
        } catch (error: any) {
            notify(error.message || 'Delete failed', { type: 'error' });
        }
    };

    return (
        <Fragment>
            <RAButton label="Delete" onClick={() => setOpen(true)} startIcon={<DeleteIcon />} disabled={isLoading} />
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>Delete this reprocessed listing?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export const ProductRecycleList: FC<ListProps> = (props) => {
    const { isSeller, sellerId } = useRecycleSellerContext();
    useAuthorize([UserRole.Admin, UserRole.Seller]);

    const listFilter = useMemo(
        () => (isSeller && sellerId ? { seller_id: sellerId } : {}),
        [isSeller, sellerId]
    );

    return (
        <List {...props} sort={{ field: 'id', order: 'DESC' }} filter={listFilter} filters={isSeller ? undefined : <RecycleListFilters />}>
            <Datagrid rowStyle={isSeller ? undefined : deletedRowStyle}>
                <TextField source="id" label="ID" />
                <TextField source="title" label="Title" />
                <StatusField label="Status" />
                <TextField source="category_name" label="Category" />
                <TextField source="sub_category_name" label="Sub Category" />
                <TextField source="state_name" label="State" />
                {!isSeller && <TextField source="seller_name" label="Seller" />}
                <NumberField source="price" label="Price" options={{ style: 'currency', currency: 'INR' }} />
                {!isSeller && <ApproveButton />}
                {!isSeller && <RejectButton />}
                <EditRecycleButton />
                <DeleteWithConfirmButton />
            </Datagrid>
        </List>
    );
};

export default ProductRecycleList;
